import React from 'react';
import { getTheme, useThemeMode } from '../theme';
import type { MarkCalendarEvent } from '../api.ts';
import type { TranscriptEntry, HuntObsRow, HuntKillRow } from '../types';
import { HuntMediaSection } from './HuntMediaSection';
import { huntSaveBlocker, rowCount, totalKillCount } from './huntSummarySave';

// ── Helper functions (internal to this modal) ────────────────────

function kwInLine(ll: string, kw: string): boolean {
  return new RegExp(`(?:^|[\\s,;.!?\\-—/])${kw}(?=$|[\\s,;.!?\\-—/])`, 'i').test(ll);
}

function parseHuntTranscript(transcription: string): {antalDrev: number; observations: string[]; kills: string[]} {
  if (!transcription) return {antalDrev: 0, observations: [], kills: []};
  const text = transcription.toLowerCase();
  const numberedDrev = new Set<string>();
  for (const m of text.matchAll(/\bdrev\s+(?:\d+|ett|två|tre|fyra|fem|sex|sju|åtta|nio|tio)\b/g)) numberedDrev.add(m[0].trim());
  let newDrevCount = 0;
  for (const _m of text.matchAll(/\bnytt\s+drev\b|\bkör\s+igång\b|\bsätt\s+igång\b|\bstart\s+(?:av\s+)?drev\b|\bdrev\s+start\b/g)) newDrevCount++;
  let antalDrev = Math.max(numberedDrev.size, newDrevCount);
  if (antalDrev === 0 && text.includes('drev')) antalDrev = 1;
  const gameAnimals = [
    {sv: 'Älg', kw: ['älg','älgko','älgtjur','älgkalv','kviga']},
    {sv: 'Dovhjort', kw: ['dovhjort','dovko','dovtjur','dovkalv','dovhind','dovspets']},
    {sv: 'Kronhjort', kw: ['kronhjort','kronko','krondj','kronkalv','kapitalhjort']},
    {sv: 'Rådjur', kw: ['rådjur','råbock','råget','råkid','rådjursget']},
    {sv: 'Vildsvin', kw: ['vildsvin','galt','sugga','smågris','kolting','årsgris']},
    {sv: 'Mufflon', kw: ['mufflon','bagge']},
    {sv: 'Björn', kw: ['björn']},
    {sv: 'Hare', kw: ['hare']},
    {sv: 'Fasan', kw: ['fasan']},
    {sv: 'Orre', kw: ['orre']},
    {sv: 'Tjäder', kw: ['tjäder']},
    {sv: 'Andfågel', kw: ['andfågel','gräsand','kricka','bläsand']},
    {sv: 'Kanadagås', kw: ['kanadagås','grågås','gäss']},
    {sv: 'Räv', kw: ['räv']},
    {sv: 'Grävling', kw: ['grävling']},
    {sv: 'Bäver', kw: ['bäver']},
    {sv: 'Mink', kw: ['mink']},
    {sv: 'Mårdhund', kw: ['mårdhund']},
  ];
  const killPat = /\b(sköt|skjutit|skjuten|skjutit på|påskjut|påskjuten|skott|fällt|fälld|träffad|nedlagd|nedlagt|knäckt|tog ner|prick|skutt|lagt ned|ligger|liggande|är nere|var nere|döende|dött|dog)\b/;
  const observations: string[] = [], kills: string[] = [];
  const lastKillLine: Record<string,number> = {};
  const lines = transcription.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const ll = lines[i].toLowerCase();
    const isKill = killPat.test(ll);
    for (const g of gameAnimals) {
      if (g.kw.some(k => kwInLine(ll, k))) {
        if (isKill) {
          if (!kills.includes(g.sv)) kills.push(g.sv);
          lastKillLine[g.sv] = i;
        } else {
          // Suppress obs if a kill for this species was within the last 3 lines
          const lastKill = lastKillLine[g.sv];
          if (lastKill != null && i - lastKill <= 3) continue;
          if (!kills.includes(g.sv) && !observations.includes(g.sv)) observations.push(g.sv);
        }
      }
    }
  }
  return {antalDrev, observations, kills};
}

function parseTranscriptEntries(entries: TranscriptEntry[]): {antalDrev: number; obsRows: HuntObsRow[]; killRows: HuntKillRow[]} {
  if (!entries || entries.length === 0) return {antalDrev: 0, obsRows: [], killRows: []};
  const gameAnimals = [
    {sv: 'Älg', kw: ['älg','älgko','älgtjur','älgkalv','kviga']},
    {sv: 'Dovhjort', kw: ['dovhjort','dovko','dovtjur','dovkalv','dovhind','dovspets']},
    {sv: 'Kronhjort', kw: ['kronhjort','kronko','krondj','kronkalv','kapitalhjort']},
    {sv: 'Rådjur', kw: ['rådjur','råbock','råget','råkid','rådjursget']},
    {sv: 'Vildsvin', kw: ['vildsvin','galt','sugga','smågris','kolting','årsgris']},
    {sv: 'Mufflon', kw: ['mufflon','bagge']},
    {sv: 'Björn', kw: ['björn']},
    {sv: 'Hare', kw: ['hare']},
    {sv: 'Fasan', kw: ['fasan']},
    {sv: 'Orre', kw: ['orre']},
    {sv: 'Tjäder', kw: ['tjäder']},
    {sv: 'Andfågel', kw: ['andfågel','gräsand','kricka','bläsand']},
    {sv: 'Kanadagås', kw: ['kanadagås','grågås','gäss']},
    {sv: 'Räv', kw: ['räv']},
    {sv: 'Grävling', kw: ['grävling']},
    {sv: 'Bäver', kw: ['bäver']},
    {sv: 'Mink', kw: ['mink']},
    {sv: 'Mårdhund', kw: ['mårdhund']},
  ];
  const killPat = /\b(sköt|skjutit|skjuten|skjutit på|påskjut|påskjuten|skott|fällt|fälld|träffad|nedlagd|nedlagt|knäckt|tog ner|prick|skutt|lagt ned|ligger|liggande|är nere|var nere|döende|dött|dog)\b/;
  const fullText = entries.map(e => e.text).join('\n').toLowerCase();
  const numberedDrev = new Set<string>();
  for (const m of fullText.matchAll(/\bdrev\s+(?:\d+|ett|två|tre|fyra|fem|sex|sju|åtta|nio|tio)\b/g)) numberedDrev.add(m[0].trim());
  let newDrevCount = 0;
  for (const _m of fullText.matchAll(/\bnytt\s+drev\b|\bkör\s+igång\b|\bsätt\s+igång\b|\bstart\s+(?:av\s+)?drev\b|\bdrev\s+start\b/g)) newDrevCount++;
  let antalDrev = Math.max(numberedDrev.size, newDrevCount);
  if (antalDrev === 0 && fullText.includes('drev')) antalDrev = 1;
  const obsRows: HuntObsRow[] = [];
  const killRows: HuntKillRow[] = [];
  // Track recent kills per species to suppress follow-up obs entries (120 s window)
  const lastKillTs = new Map<string, number>();
  const SUPPRESS_MS = 120_000;
  for (const entry of entries) {
    const ll = entry.text.toLowerCase();
    const isKill = killPat.test(ll);
    for (const g of gameAnimals) {
      if (g.kw.some(k => kwInLine(ll, k))) {
        if (isKill) {
          lastKillTs.set(g.sv, entry.ts);
          let kon = 'Okänt';
          if (/\b(tjur|hane|galt|bock)\b/.test(ll)) kon = 'Hane';
          else if (/\b(ko|hona|sugga|get|hind)\b/.test(ll)) kon = 'Hona';
          else if (/\b(kalv|kultingen?|kid|unge)\b/.test(ll)) kon = 'Kalv';
          killRows.push({art: g.sv, speaker: entry.speaker, lat: entry.lat, lng: entry.lng, ts: entry.ts, count: 1, kon});
        } else {
          // Undertryck obs-rad om kill nyligen detekterades för samma art
          const prevKill = lastKillTs.get(g.sv);
          if (prevKill != null && entry.ts - prevKill < SUPPRESS_MS) continue;
          obsRows.push({art: g.sv, speaker: entry.speaker, lat: entry.lat, lng: entry.lng, ts: entry.ts, count: 1});
        }
      }
    }
  }
  return {antalDrev, obsRows, killRows};
}

// ── Constants ────────────────────────────────────────────────────

const GAME_TYPES_HUNT=[
  'Älg — Tjur >10 taggar','Älg — Tjur 4–10 taggar','Älg — Tjur 2–4 taggar','Älg — Kviga','Älg — Ko','Älg — Kalv',
  'Kronhjort — Kapitalhjort','Kronhjort — Hjort under 10 taggar','Kronhjort — Spets','Kronhjort — Hind','Kronhjort — Kalv',
  'Dovhjort — Dovspets','Dovhjort — Dovhelskovel','Dovhjort — Dovhalvskovel','Dovhjort — Dovhind','Dovhjort — Dovkalv',
  'Rådjur — Råbock medalj','Rådjur — Råbock 6-taggare','Rådjur — Råbock 4-taggare','Rådjur — Råbock spets','Rådjur — Råget','Rådjur — Råkid',
  'Vildsvin — Sugga','Vildsvin — Galt','Vildsvin — Årsgris',
  'Mufflon — Kapital Bagge','Mufflon — Mindre bagge','Mufflon — Hona','Mufflon — Lamm',
  'Räv','Grävling','Hare','Björn','Fasan','Orre','Tjäder','Skogsfågel','Kanadagås','Andfågel','Bäver','Mink','Mårdhund',
];

// Map family-level names (from transcript detection) to a sensible default subcat
const FAMILY_DEFAULT_SUBCAT: Record<string,string> = {
  'Älg':'Älg — Ko','Kronhjort':'Kronhjort — Hjort under 10 taggar','Dovhjort':'Dovhjort — Dovhind',
  'Rådjur':'Rådjur — Råget','Vildsvin':'Vildsvin — Galt','Mufflon':'Mufflon — Hona',
  'And':'Andfågel',
};

function resolveHuntArt(art: string): string { return FAMILY_DEFAULT_SUBCAT[art] ?? art; }

const SEX_OPTIONS_HUNT=['Okänt','Hane','Hona','Kalv','Ungt'];

// suppress unused warnings for exported helpers not used directly
void parseTranscriptEntries;

// ── Component ────────────────────────────────────────────────────

export function CompletedHuntModal({event,onClose,onUpdateGoals}:{event:MarkCalendarEvent;onClose:()=>void;onUpdateGoals?:(kills:{gameType:string;count:number;observations?:any[];kills?:any[]}[])=>Promise<void>}){
  const { mode } = useThemeMode(); const T = getTheme(mode);
  const mapObs:any[]=event.observationsJson?JSON.parse(event.observationsJson):[];
  const mapKills:any[]=event.killsJson?JSON.parse(event.killsJson):[];
  const [activeTab,setActiveTab]=React.useState<'summary'|'transcription'|'recording'>('summary');
  const [saving,setSaving]=React.useState(false);
  const [saved,setSaved]=React.useState(false);

  const parsed=React.useMemo(()=>parseHuntTranscript(event.transcription||''),[event.transcription]);
  const [drevCount,setDrevCount]=React.useState<number>(()=>parsed.antalDrev);

  type ObsRow={id:string;art:string;antal:number;source:'karta'|'transkribering'|'manuell';speaker?:string;lat?:number;lng?:number;ts?:number};
  const [obsRows,setObsRows]=React.useState<ObsRow[]>(()=>{
    const rows:ObsRow[]=[];
    mapObs.forEach((o:any,i:number)=>rows.push({id:`k${i}`,art:resolveHuntArt(o.label||'Observation'),antal:1,source:o.speaker?'transkribering':'karta',speaker:o.speaker,lat:o.lat,lng:o.lng,ts:o.ts}));
    parsed.observations.filter(a=>!mapObs.some((o:any)=>(o.label||'').toLowerCase()===a.toLowerCase()))
      .forEach((a,i)=>rows.push({id:`to${i}`,art:resolveHuntArt(a),antal:1,source:'transkribering'}));
    return rows;
  });
  const [newObsArt,setNewObsArt]=React.useState(GAME_TYPES_HUNT[0]);
  const [newObsAntal,setNewObsAntal]=React.useState(1);

  type KillRow={id:string;art:string;antal:number;kon:string;source:'karta'|'transkribering'|'manuell'|'bekräftad';speaker?:string;lat?:number;lng?:number;ts?:number};
  const [killRows,setKillRows]=React.useState<KillRow[]>(()=>{
    const rows:KillRow[]=[];
    // Confirmed kills first (source='bekräftad' stored in killsJson)
    mapKills.filter((k:any)=>k.source==='bekräftad').forEach((k:any,i:number)=>rows.push({id:`bk${i}`,art:resolveHuntArt(k.label||'Fällt vilt'),antal:1,kon:k.kon||'Okänt',source:'bekräftad',lat:k.lat,lng:k.lng,ts:k.ts}));
    // Map pins (source='karta')
    mapKills.filter((k:any)=>k.source==='karta'||(k.source==null&&!k.speaker)).forEach((k:any,i:number)=>rows.push({id:`pk${i}`,art:resolveHuntArt(k.label||'Fällt vilt'),antal:1,kon:k.kon||'Okänt',source:'karta',lat:k.lat,lng:k.lng,ts:k.ts}));
    // Transcript/chat kills (unconfirmed)
    mapKills.filter((k:any)=>k.source==='transkribering'||k.speaker).forEach((k:any,i:number)=>rows.push({id:`tk${i}`,art:resolveHuntArt(k.label||'Fällt vilt'),antal:1,kon:k.kon||'Okänt',source:'transkribering',speaker:k.speaker,lat:k.lat,lng:k.lng,ts:k.ts}));
    // Legacy fallback for old events without source field
    if(mapKills.length===0){
      parsed.kills.forEach((a,i)=>rows.push({id:`lk${i}`,art:resolveHuntArt(a),antal:1,kon:'Okänt',source:'transkribering'}));
    }
    return rows;
  });
  const [newKillArt,setNewKillArt]=React.useState(GAME_TYPES_HUNT[0]);
  const [newKillAntal,setNewKillAntal]=React.useState(1);
  const [newKillKon,setNewKillKon]=React.useState('Okänt');

  const iS:React.CSSProperties={background:T.surface,border:`1px solid ${T.border}`,color:T.text,padding:'4px 8px',borderRadius:4,fontFamily:'inherit',fontSize:13};

  const srcBadge=(s:string)=>{
    const cfg:{[k:string]:{bg:string;color:string;text:string}}={
      bekräftad:{bg:'rgba(97,216,54,0.18)',color:'#61D836',text:'✓ Bekräftad'},
      karta:{bg:'rgba(0,100,255,0.12)',color:'#4a9eff',text:'Karta'},
      transkribering:{bg:'rgba(238,143,9,0.12)',color:'#EE8F09',text:'Ej bekräftad'},
      manuell:{bg:'rgba(150,150,150,0.12)',color:'#aaa',text:'Manuell'},
    };
    const c=cfg[s]||cfg.manuell;
    return <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:8,background:c.bg,color:c.color,whiteSpace:'nowrap'}}>{c.text}</span>;
  };

  const doSave=async()=>{
    if(!onUpdateGoals||saving||saved)return;
    setSaving(true);
    try{
      const counts:Record<string,number>={};
      killRows.forEach(k=>{counts[k.art]=(counts[k.art]||0)+rowCount(k.antal)});
      await onUpdateGoals(Object.entries(counts).map(([gameType,count])=>({gameType,count,observations:obsRows.filter(o=>o.art===gameType).map(o=>({speaker:o.speaker,lat:o.lat,lng:o.lng,ts:o.ts,antal:o.antal})),kills:killRows.filter(k=>k.art===gameType).map(k=>({speaker:k.speaker,lat:k.lat,lng:k.lng,ts:k.ts,kon:k.kon}))})));
      setSaved(true);
    }finally{setSaving(false);}
  };

  const totalObs=totalKillCount(obsRows);
  const totalKills=totalKillCount(killRows);
  const saveBlocker=huntSaveBlocker(killRows);

  return(
    <div style={{position:'fixed',inset:0,zIndex:10000,background:'rgba(0,0,0,0.75)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:T.bg,borderRadius:12,maxWidth:660,width:'100%',maxHeight:'92vh',overflow:'hidden',border:`1px solid ${T.border}`,boxShadow:'0 16px 56px rgba(0,0,0,0.65)',display:'flex',flexDirection:'column'}}>

        {/* Header */}
        <div style={{padding:'16px 20px 12px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <div>
            <div style={{fontSize:17,fontWeight:700,color:T.text}}>{event.title}</div>
            <div style={{fontSize:12,color:T.textSub,marginTop:2}}>
              {new Date(event.startTime).toLocaleDateString('sv-SE',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
              {event.endedAt&&` — Avslutad ${new Date(event.endedAt).toLocaleTimeString('sv-SE',{hour:'2-digit',minute:'2-digit'})}`}
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:10,background:'rgba(97,216,54,0.18)',color:'#61D836'}}>Genomförd</span>
            <button onClick={onClose} style={{background:'none',border:'none',color:T.textSub,fontSize:20,cursor:'pointer',padding:'2px 6px',lineHeight:1}}>×</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{display:'flex',borderBottom:`1px solid ${T.border}`,padding:'0 20px',flexShrink:0}}>
          {([['summary','Sammanfattning'],['transcription','Transkribering'],['recording','Inspelning']] as const).map(([k,l])=>(
            <button key={k} onClick={()=>setActiveTab(k)} style={{
              background:'none',border:'none',borderBottom:activeTab===k?`2px solid ${T.amber}`:'2px solid transparent',
              color:activeTab===k?T.amber:T.textSub,fontSize:12,fontWeight:600,padding:'10px 14px',cursor:'pointer',fontFamily:'inherit',
            }}>{l}</button>
          ))}
        </div>

        {/* Content */}
        <div style={{padding:'18px 20px',flex:1,overflowY:'auto',overflowX:'hidden'}}>

          {/* ─ SUMMARY ─ */}
          {activeTab==='summary'&&(
            <div style={{display:'flex',flexDirection:'column',gap:16}}>

              {/* QJ2: Q:s tolkade sammanfattning */}
              {event.summary&&(
                <div data-q-summary style={{background:'rgba(80,120,200,0.12)',border:'1px solid rgba(120,160,255,0.35)',borderRadius:8,padding:'10px 14px'}}>
                  <div style={{fontSize:11,fontWeight:800,color:'#7f9fe0',marginBottom:6,letterSpacing:'0.04em',textTransform:'uppercase'}}>Q:s sammanfattning</div>
                  <div style={{color:T.text,fontSize:13,lineHeight:1.55,whiteSpace:'pre-wrap'}}>{event.summary}</div>
                </div>
              )}

              {/* Antal drev */}
              <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,overflow:'hidden'}}>
                <div style={{padding:'9px 14px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:26,height:26,borderRadius:5,background:'rgba(238,143,9,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,color:T.amber,fontSize:13}}>D</div>
                  <div style={{fontWeight:700,color:T.text,fontSize:14,flex:1}}>Antal drev</div>
                  {parsed.antalDrev>0&&<span style={{fontSize:10,color:T.textMuted,fontStyle:'italic'}}>Extraherat från transkribering</span>}
                </div>
                <div style={{padding:'12px 14px',display:'flex',alignItems:'center',gap:14}}>
                  <button onClick={()=>setDrevCount(c=>Math.max(0,c-1))} style={{width:32,height:32,borderRadius:6,border:`1px solid ${T.border}`,background:T.bg,color:T.text,fontSize:18,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'inherit'}}>−</button>
                  <div style={{fontSize:30,fontWeight:800,color:T.amber,minWidth:36,textAlign:'center'}}>{drevCount}</div>
                  <button onClick={()=>setDrevCount(c=>c+1)} style={{width:32,height:32,borderRadius:6,border:`1px solid ${T.border}`,background:T.bg,color:T.text,fontSize:18,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'inherit'}}>+</button>
                  <span style={{color:T.textSub,fontSize:13,marginLeft:4}}>{drevCount===1?'drev genomfört':'drev genomförda'}</span>
                </div>
              </div>

              {/* Observationer */}
              <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,overflow:'hidden'}}>
                <div style={{padding:'9px 14px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:26,height:26,borderRadius:5,background:'rgba(0,100,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,color:'#4a9eff',fontSize:13}}>O</div>
                  <div style={{fontWeight:700,color:T.text,fontSize:14,flex:1}}>Observationer av vilt</div>
                  <span style={{fontSize:11,fontWeight:700,color:'#4a9eff',background:'rgba(0,100,255,0.12)',padding:'2px 9px',borderRadius:10}}>{totalObs} st</span>
                </div>
                <div style={{padding:'10px 14px',display:'flex',flexDirection:'column',gap:5}}>
                  {obsRows.length===0&&<div style={{color:T.textSub,fontSize:13,fontStyle:'italic',padding:'4px 0'}}>Inga observationer registrerade</div>}
                  {obsRows.map((row,i)=>(
                    <div key={row.id} style={{display:'flex',flexDirection:'column',gap:3,padding:'6px 10px',background:T.bg,borderRadius:5,border:`1px solid ${T.border}`}}>
                      <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:7}}>
                        <select value={row.art} onChange={e=>setObsRows(r=>r.map((x,j)=>j===i?{...x,art:e.target.value}:x))} style={{...iS,flex:'1 1 160px',minWidth:0,fontWeight:600}}>{GAME_TYPES_HUNT.map(g=><option key={g}>{g}</option>)}</select>
                        <input type="number" min="1" value={row.antal} onChange={e=>setObsRows(r=>r.map((x,j)=>j===i?{...x,antal:rowCount(Number(e.target.value))}:x))} style={{...iS,width:50,textAlign:'center',padding:'3px 5px'}}/>
                        <span style={{fontSize:11,color:T.textSub}}>st</span>
                        {srcBadge(row.source)}
                        <button onClick={()=>setObsRows(r=>r.filter((_,j)=>j!==i))} style={{background:'none',border:'none',color:T.textMuted,fontSize:16,cursor:'pointer',padding:'0 2px',lineHeight:1}}>×</button>
                      </div>
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {row.speaker&&<div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontSize:10,color:T.textMuted}}>Rapportör:</span><input value={row.speaker} onChange={e=>setObsRows(r=>r.map((x,j)=>j===i?{...x,speaker:e.target.value}:x))} style={{...iS,fontSize:11,padding:'2px 6px',width:120}}/></div>}
                        {(row.lat!=null&&row.lng!=null)&&<div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontSize:10,color:T.textMuted}}>Koord:</span><input value={row.lat!=null?row.lat.toFixed(5):''} onChange={e=>setObsRows(r=>r.map((x,j)=>j===i?{...x,lat:parseFloat(e.target.value)||undefined}:x))} style={{...iS,fontSize:11,padding:'2px 6px',width:90}} placeholder="lat"/><input value={row.lng!=null?row.lng.toFixed(5):''} onChange={e=>setObsRows(r=>r.map((x,j)=>j===i?{...x,lng:parseFloat(e.target.value)||undefined}:x))} style={{...iS,fontSize:11,padding:'2px 6px',width:90}} placeholder="lng"/></div>}
                        {row.ts&&<span style={{fontSize:10,color:T.textMuted}}>{new Date(row.ts).toLocaleTimeString('sv-SE',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}</span>}
                      </div>
                    </div>
                  ))}
                  <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:7,paddingTop:obsRows.length>0?6:0,borderTop:obsRows.length>0?`1px dashed ${T.border}`:'none',marginTop:obsRows.length>0?2:0}}>
                    <select value={newObsArt} onChange={e=>setNewObsArt(e.target.value)} style={{...iS,flex:'1 1 160px',minWidth:0}}>{GAME_TYPES_HUNT.map(g=><option key={g}>{g}</option>)}</select>
                    <input type="number" min="1" value={newObsAntal} onChange={e=>setNewObsAntal(rowCount(Number(e.target.value)))} style={{...iS,width:50,textAlign:'center'}}/>
                    <span style={{fontSize:11,color:T.textSub}}>st</span>
                    <button onClick={()=>{setObsRows(r=>[...r,{id:`mo${Date.now()}`,art:newObsArt,antal:newObsAntal,source:'manuell'}]);}} style={{padding:'5px 12px',background:'rgba(0,100,255,0.8)',color:'#fff',border:'none',borderRadius:4,fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>+ Lägg till</button>
                  </div>
                </div>
              </div>

              {/* Fällt vilt */}
              <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:8,overflow:'hidden'}}>
                <div style={{padding:'9px 14px',borderBottom:`1px solid ${T.border}`,display:'flex',alignItems:'center',gap:8}}>
                  <div style={{width:26,height:26,borderRadius:5,background:'rgba(229,57,53,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,color:'#E53935',fontSize:13}}>F</div>
                  <div style={{fontWeight:700,color:T.text,fontSize:14,flex:1}}>Fällt vilt</div>
                  <span style={{fontSize:11,fontWeight:700,color:'#E53935',background:'rgba(229,57,53,0.12)',padding:'2px 9px',borderRadius:10}}>{totalKills} st</span>
                </div>
                <div style={{padding:'10px 14px',display:'flex',flexDirection:'column',gap:5}}>
                  {killRows.length===0&&<div style={{color:T.textSub,fontSize:13,fontStyle:'italic',padding:'4px 0'}}>Inget fällt vilt registrerat</div>}
                  {killRows.map((row,i)=>(
                    <div key={row.id} style={{display:'flex',flexDirection:'column',gap:3,padding:'6px 10px',background:row.source==='bekräftad'?'rgba(97,216,54,0.04)':T.bg,borderRadius:5,border:`1px solid ${row.source==='bekräftad'?'rgba(97,216,54,0.3)':T.border}`}}>
                      <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:7}}>
                        <select value={row.art} onChange={e=>setKillRows(r=>r.map((x,j)=>j===i?{...x,art:e.target.value}:x))} style={{...iS,flex:'1 1 160px',minWidth:0,fontWeight:600}}>{GAME_TYPES_HUNT.map(g=><option key={g}>{g}</option>)}</select>
                        <input type="number" min="1" value={row.antal} onChange={e=>setKillRows(r=>r.map((x,j)=>j===i?{...x,antal:rowCount(Number(e.target.value))}:x))} style={{...iS,width:50,textAlign:'center',padding:'3px 5px'}}/>
                        <span style={{fontSize:11,color:T.textSub}}>st</span>
                        <select value={row.kon} onChange={e=>setKillRows(r=>r.map((x,j)=>j===i?{...x,kon:e.target.value}:x))} style={{...iS,width:76}}>
                          {SEX_OPTIONS_HUNT.map(s=><option key={s}>{s}</option>)}
                        </select>
                        {srcBadge(row.source)}
                        <button onClick={()=>setKillRows(r=>r.filter((_,j)=>j!==i))} style={{background:'none',border:'none',color:T.textMuted,fontSize:16,cursor:'pointer',padding:'0 2px',lineHeight:1}}>×</button>
                      </div>
                      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                        {row.speaker&&<div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontSize:10,color:T.textMuted}}>Rapportör:</span><input value={row.speaker} onChange={e=>setKillRows(r=>r.map((x,j)=>j===i?{...x,speaker:e.target.value}:x))} style={{...iS,fontSize:11,padding:'2px 6px',width:120}}/></div>}
                        {(row.lat!=null&&row.lng!=null)&&<div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontSize:10,color:T.textMuted}}>Koord:</span><input value={row.lat!=null?row.lat.toFixed(5):''} onChange={e=>setKillRows(r=>r.map((x,j)=>j===i?{...x,lat:parseFloat(e.target.value)||undefined}:x))} style={{...iS,fontSize:11,padding:'2px 6px',width:90}} placeholder="lat"/><input value={row.lng!=null?row.lng.toFixed(5):''} onChange={e=>setKillRows(r=>r.map((x,j)=>j===i?{...x,lng:parseFloat(e.target.value)||undefined}:x))} style={{...iS,fontSize:11,padding:'2px 6px',width:90}} placeholder="lng"/></div>}
                        {row.ts&&<span style={{fontSize:10,color:T.textMuted}}>{new Date(row.ts).toLocaleTimeString('sv-SE',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}</span>}
                      </div>
                    </div>
                  ))}
                  <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:7,paddingTop:killRows.length>0?6:0,borderTop:killRows.length>0?`1px dashed ${T.border}`:'none',marginTop:killRows.length>0?2:0}}>
                    <select value={newKillArt} onChange={e=>setNewKillArt(e.target.value)} style={{...iS,flex:'1 1 160px',minWidth:0}}>{GAME_TYPES_HUNT.map(g=><option key={g}>{g}</option>)}</select>
                    <input type="number" min="1" value={newKillAntal} onChange={e=>setNewKillAntal(rowCount(Number(e.target.value)))} style={{...iS,width:50,textAlign:'center'}}/>
                    <span style={{fontSize:11,color:T.textSub}}>st</span>
                    <select value={newKillKon} onChange={e=>setNewKillKon(e.target.value)} style={{...iS,width:76}}>
                      {SEX_OPTIONS_HUNT.map(s=><option key={s}>{s}</option>)}
                    </select>
                    <button onClick={()=>{setKillRows(r=>[...r,{id:`mk${Date.now()}`,art:newKillArt,antal:newKillAntal,kon:newKillKon,source:'manuell'}]);}} style={{padding:'5px 12px',background:'rgba(229,57,53,0.85)',color:'#fff',border:'none',borderRadius:4,fontWeight:700,fontSize:12,cursor:'pointer',fontFamily:'inherit',whiteSpace:'nowrap'}}>+ Lägg till</button>
                  </div>
                </div>
              </div>

              <HuntMediaSection event={event}/>

              {/* Update plan action */}
              {onUpdateGoals&&(
                <div style={{display:'flex',alignItems:'center',flexWrap:'wrap',gap:12,padding:'12px 16px',background:saved?'rgba(97,216,54,0.08)':'rgba(238,143,9,0.06)',border:`1px solid ${saved?'rgba(97,216,54,0.3)':'rgba(238,143,9,0.22)'}`,borderRadius:8}}>
                  {saved?(
                    <div style={{color:'#61D836',fontWeight:700,fontSize:14}}>Avskjutningsplan uppdaterad!</div>
                  ):(
                    <>
                      <div style={{flex:'1 1 200px',minWidth:0}}>
                        <div style={{fontWeight:700,color:T.text,fontSize:13}}>Uppdatera avskjutningsplan</div>
                        {saveBlocker
                          ?<div id="hunt-save-blocker" role="status" style={{color:T.danger,fontSize:12,fontWeight:600,marginTop:2}}>{saveBlocker}</div>
                          :<div style={{color:T.textSub,fontSize:12,marginTop:2}}>{totalKills} fällda djur sparas i planen</div>}
                      </div>
                      <button onClick={doSave} disabled={saving||!!saveBlocker} aria-describedby={saveBlocker?'hunt-save-blocker':undefined} style={{padding:'8px 20px',background:T.amber,color:'#000',border:'none',borderRadius:6,fontWeight:700,fontSize:13,cursor:saveBlocker?'not-allowed':'pointer',fontFamily:'inherit',opacity:saveBlocker?0.45:1,whiteSpace:'nowrap'}}>
                        {saving?'Sparar...':'Spara & uppdatera plan'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ─ TRANSCRIPTION ─ */}
          {activeTab==='transcription'&&(
            <div>
              {event.transcription?(
                <div style={{color:T.text,fontSize:12.5,lineHeight:1.85,whiteSpace:'pre-wrap',fontFamily:'monospace',background:T.surface,padding:16,borderRadius:6,maxHeight:480,overflow:'auto'}}>{event.transcription}</div>
              ):(
                <div style={{color:T.textSub,fontSize:13,fontStyle:'italic'}}>Ingen transkribering tillgänglig. Aktivera mikrofon under jakten för automatisk transkribering.</div>
              )}
            </div>
          )}

          {/* ─ RECORDING ─ */}
          {activeTab==='recording'&&(
            <div>
              {event.recordingUrl?(
                <div>
                  <div style={{color:T.text,fontSize:14,marginBottom:12}}>Inspelning av jaktmötet</div>
                  <a href={event.recordingUrl} target="_blank" rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',gap:8,padding:'10px 20px',background:T.amber,color:'#000',borderRadius:6,fontWeight:700,fontSize:13,textDecoration:'none'}}>Spela upp inspelning</a>
                </div>
              ):(
                <div style={{color:T.textSub,fontSize:13,fontStyle:'italic'}}>Ingen inspelning tillgänglig.</div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
